<script lang="ts" setup>
import { useLanguage } from '@nzyme/vue-i18n/useLanguage.js';
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';

import * as l from './CtaSection.loc.js';
import PlusGrid from './PlusGrid.vue';
import StartConsultationButton from './StartConsultationButton.vue';

const t = useTranslate();
const lang = useLanguage();
</script>

<template>
  <section
    id="cta"
    :class="css.ctaSection"
    data-width="full"
  >
    <div :class="css.content">
      <div :class="css.inner">
        <h2 :class="css.heading">{{ t(l.ctaHeading) }}</h2>
        <p :class="css.description">{{ t(l.ctaDescription) }}</p>
        <div :class="css.actions">
          <StartConsultationButton />
        </div>
      </div>
    </div>
    <div :class="css.graphic">
      <PlusGrid
        variant="full"
        :class="css.plusGrid"
      />
      <NuxtPicture
        :src="lang === 'pl' ? '/img/app-pl.png' : '/img/app-en.png'"
        :class="css.graphic_img"
        :alt="t(l.iphoneShowingHealed)"
        loading="lazy"
        :img-attrs="{
          class: css.graphic_img,
        }"
      />
    </div>
  </section>
</template>

<style lang="scss" module="css">
$column-breakpoint: 920px;

.ctaSection {
  display: grid;
  gap: 0;
  padding: 0;
  grid-template-columns: 1fr;
  background: linear-gradient(135deg, $color-patient-200, $color-doctor-200);

  @include media-min($column-breakpoint) {
    grid-template-columns: 1fr 1fr;
    height: 50vw;
    flex: 0;
  }

  @include media-min(2 * $column-breakpoint) {
    height: 900px;
    max-height: 900px;
  }
}

.content {
  padding: var(--space-large) var(--padding-global) var(--space-small);
  display: flex;
  flex-direction: column;
  gap: var(--space-small);

  @media screen and (min-width: 768px) {
    padding: var(--space-small) var(--padding-global);
  }

  @media screen and (min-width: 768px) and (max-width: 1023px) {
    padding: var(--space-small) var(--padding-global);
  }

  @media screen and (min-width: 1024px) {
    padding: var(--space-medium) var(--padding-global);
  }
}

.inner {
  text-align: center;
  margin: auto;

  @media screen and (min-width: 768px) {
    text-align: left;
  }
}

.heading {
  margin: 0;
  font-size: var(--font-size-heading-1);
  font-weight: $font-weight-600;
  line-height: $line-height-heading-1;
  letter-spacing: $letter-spacing-heading-1;
}

.description {
  margin: var(--space-xxxsmall) 0 0 0;
  font-size: var(--font-size-heading-6);
  line-height: 150%;
  color: $color-gray-700;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-xxsmall);
  justify-content: center;
  margin-top: var(--space-small);

  @media screen and (min-width: 768px) {
    justify-content: start;
  }
}

.graphic {
  position: relative;
  container-type: inline-size;
  overflow: hidden;
  margin: 0;
  padding: 0;
  height: 100%;

  @include media-max($column-breakpoint) {
    height: 100vw;
  }

  &_img {
    position: relative;
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center bottom;
  }
}

.plusGrid {
  --cols: 9;

  @media screen and (min-width: 768px) {
    --cols: 9;
  }

  @media screen and (min-width: 1280px) {
    --cols: 13;
  }
}
</style>
